import { Heart, ListMusic, PartyPopper, Share2, Sparkles } from 'lucide-react';
import { nextEventContactPath } from '../../utils/endOfShow';
import EventAwardsCard from '../games/EventAwardsCard';
import type { EventAwards } from '../../services/eventAwards';

interface EventEndedViewProps {
  eventId: string;
  eventName: string;
  eventUrl: string;
  playedSongs: Array<{ id: string; title: string; artist: string }>;
  canLeaveFeedback: boolean;
  onLeaveFeedback: () => void;
  onSharePlaylist: () => void;
  onShareVoteBeats: () => void;
  awards?: EventAwards | null;
  attendeeId?: string;
}

const PREVIEW_COUNT = 5;

// The one end-of-event screen (ITER-199): a summary of the night and the lasting links
export function EventEndedView({
  eventId,
  eventName,
  eventUrl,
  playedSongs,
  canLeaveFeedback,
  onLeaveFeedback,
  onSharePlaylist,
  onShareVoteBeats,
  awards,
  attendeeId,
}: EventEndedViewProps) {
  const preview = playedSongs.slice(0, PREVIEW_COUNT);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="event-ended-view">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <PartyPopper className="w-14 h-14 mx-auto text-primary mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">That&rsquo;s a wrap! 🎉</h1>
          <p className="text-gray-600 dark:text-gray-400">{eventName} has ended. Thanks for being part of it.</p>
        </div>

        {awards && awards.revealed.length > 0 && <EventAwardsCard awards={awards} attendeeId={attendeeId} collapsible />}

        <div className="card p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-semibold text-gray-900 dark:text-white">Tonight&rsquo;s playlist</h2>
            <span className="text-sm text-gray-500 dark:text-gray-400" data-testid="played-count">
              {playedSongs.length === 0
                ? 'No songs were played'
                : `${playedSongs.length} song${playedSongs.length === 1 ? '' : 's'} played`}
            </span>
          </div>
          {preview.length > 0 && (
            <ol className="space-y-1.5">
              {preview.map((song) => (
                <li key={song.id} className="text-sm min-w-0">
                  <span className="font-medium text-gray-900 dark:text-white">{song.title}</span>
                  <span className="text-gray-500 dark:text-gray-400"> · {song.artist}</span>
                </li>
              ))}
            </ol>
          )}
          <a
            href={`/e/${eventUrl}/playlist`}
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium"
          >
            <ListMusic className="w-4 h-4" />
            See the full playlist
          </a>
        </div>

        <div className="space-y-2">
          {canLeaveFeedback && (
            <button type="button" onClick={onLeaveFeedback} className="btn btn-primary w-full flex items-center justify-center gap-2">
              <Heart className="w-4 h-4" />
              Leave feedback for the DJ
            </button>
          )}
          <button type="button" onClick={onSharePlaylist} className="btn btn-secondary w-full flex items-center justify-center gap-2">
            <ListMusic className="w-4 h-4" />
            Share the playlist
          </button>
          <button type="button" onClick={onShareVoteBeats} className="btn btn-secondary w-full flex items-center justify-center gap-2">
            <Share2 className="w-4 h-4" />
            Share VoteBeats with a friend or your DJ
          </button>
          <a
            href={nextEventContactPath(eventId, 'ended-page')}
            className="btn btn-secondary w-full flex items-center justify-center gap-2"
            data-testid="ended-next-event-link"
          >
            <Sparkles className="w-4 h-4" />
            VoteBeats at your next event?
          </a>
        </div>
      </div>
    </div>
  );
}
